import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogTitle from "@mui/material/DialogTitle"
import FormControlLabel from "@mui/material/FormControlLabel"
import Paper from "@mui/material/Paper"
import Switch from "@mui/material/Switch"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { featuresApi } from "../axios"
import { setAdminFeatures } from "../reducer"

export const FEATURE_DESCRIPTIONS = {
  pod_dialer:
    "Hides Pods, Assign to pod and the agent dialer tabs everywhere when off.",
}

const FEATURE_TITLES = { pod_dialer: "Pod Dialer" }

/** Runtime switches for unfinished features; only the dev team can flip them. */
export default function FeaturesCard() {
  const dispatch = useDispatch()
  const [data, setData] = useState(null)
  const [pending, setPending] = useState(null) // {key, enabled}
  const [error, setError] = useState("")
  const [saving, setSaving] = useState(false)

  function apply(next) {
    setData(next)
    dispatch(
      setAdminFeatures(
        Object.fromEntries(next.features.map((f) => [f.key, f.enabled]))
      )
    )
  }

  useEffect(() => {
    featuresApi
      .get()
      .then(({ data: next }) => setData(next))
      .catch(() => setError("Could not load features."))
  }, [])

  function confirm() {
    setSaving(true)
    featuresApi
      .set(pending.key, pending.enabled)
      .then(({ data: next }) => {
        apply(next)
        setPending(null)
      })
      .catch((err) =>
        setError(err?.response?.data?.detail || "Could not change the switch.")
      )
      .finally(() => setSaving(false))
  }

  if (!data) return error ? <Alert severity="error">{error}</Alert> : null
  const canManage = Boolean(data.can_manage_features)

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Column gap={1.5}>
        <Typography variant="h6" fontWeight={700}>
          Features in development
        </Typography>
        <Alert severity="info">
          In-development features. These switches turn unfinished features on or
          off, so this card is locked to the dev team.
        </Alert>
        {!canManage ? (
          <Typography variant="body2" color="text.secondary">
            Locked to the dev team.
          </Typography>
        ) : null}
        {error ? (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}
        {data.features.map((feature) => (
          <Row
            key={feature.key}
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Column gap={0.25}>
              <Typography variant="subtitle2">
                {FEATURE_TITLES[feature.key] || feature.key}:{" "}
                {feature.enabled ? "On" : "Off"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {FEATURE_DESCRIPTIONS[feature.key] || feature.label}
              </Typography>
            </Column>
            <FormControlLabel
              label=""
              control={
                <Switch
                  checked={feature.enabled}
                  disabled={!canManage}
                  inputProps={{
                    "aria-label": FEATURE_TITLES[feature.key] || feature.key,
                  }}
                  onChange={(e) =>
                    setPending({ key: feature.key, enabled: e.target.checked })
                  }
                />
              }
            />
          </Row>
        ))}
        {data.changes?.length ? (
          <Typography variant="caption" color="text.secondary">
            Last change:{" "}
            {FEATURE_TITLES[data.changes[0].key] || data.changes[0].key} turned{" "}
            {data.changes[0].enabled ? "on" : "off"} by{" "}
            {data.changes[0].changed_by || "system"} on{" "}
            {new Date(data.changes[0].at).toLocaleString()}
          </Typography>
        ) : null}
      </Column>
      <Dialog open={Boolean(pending)} onClose={() => setPending(null)}>
        <DialogTitle>
          Turn {FEATURE_TITLES[pending?.key] || pending?.key}{" "}
          {pending?.enabled ? "on" : "off"}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {FEATURE_TITLES[pending?.key] || pending?.key}: On/Off —{" "}
            {FEATURE_DESCRIPTIONS[pending?.key]}
            {pending && !pending.enabled
              ? " Running pod sessions stop dialing and end once live calls finish."
              : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pending?.enabled ? "primary" : "error"}
            onClick={confirm}
            disabled={saving}
          >
            Turn {pending?.enabled ? "on" : "off"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}
